// Extracted verbatim from pages/coach/Schedule.jsx (2026-06-08 P3 refactor).
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, Loader2, UserPlus, Zap, Minus, Plus, ArrowRightLeft } from "lucide-react";
import {
    DAY_LABELS, DAY_LABELS_LONG, HALF_HOUR_TIMES, formatTimeDisplay,
    isoDate, startOfWeekMon, addDays, weekRangeLabel, jsDayToMonIdx,
} from "@/lib/schedule";
import { CATEGORIES, CATEGORY_LABELS } from "@/lib/workoutTaxonomy";
import { ModalShell, Field, inputClass } from "./ModalShell";
import AthleteNameLink from "@/components/coach/AthleteNameLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SlotBookingsModal({ slot, allSlots, members, isLive, onAssign, onCancel, onMove, onMarkPresent, onWalkIn, onDelete, onEdit, onClose, onBookingsChanged }) {
    const [pickerUserId, setPickerUserId] = useState("");
    const [pickerAthleteId, setPickerAthleteId] = useState("");
    const [walkinUserId, setWalkinUserId] = useState("");
    const [walkinAthleteId, setWalkinAthleteId] = useState("");
    const [bulkOpen, setBulkOpen] = useState(false);
    // Which booking (if any) is currently being moved — drives the slot picker.
    const [movingBooking, setMovingBooking] = useState(null);
    // Other open slots on the SAME day — powers the inline "Move to…" dropdown.
    const sameDaySlots = (allSlots || []).filter(
        (s) => s.date === slot.date && s.slot_id !== slot.slot_id && (s.booking_count || 0) < s.capacity,
    );

    // Sachin 2026-03-06: inline +/- for session-count adjustments. Optimistic
    // UI writes into `sessionsOverride[booking_id]` immediately, then the
    // server response reconciles it. Per-booking `adjusting` tracks in-flight
    // requests so we can disable buttons and skip stacked clicks.
    const [sessionsOverride, setSessionsOverride] = useState({});   // booking_id → number
    const [adjusting, setAdjusting] = useState({});                 // booking_id → bool

    const adjustSessions = async (b, delta) => {
        const id = b.booking_id;
        if (adjusting[id]) return;
        const current = sessionsOverride[id] != null ? sessionsOverride[id] : b.remaining_sessions;
        if (current == null) {
            toast.error("Session count unknown — refresh the schedule");
            return;
        }
        const nextOptimistic = Math.max(0, current + delta);
        // Optimistic update.
        setSessionsOverride((s) => ({ ...s, [id]: nextOptimistic }));
        setAdjusting((a) => ({ ...a, [id]: true }));
        try {
            const r = await axios.post(
                `${API}/coach/schedule/bookings/${id}/adjust-sessions`,
                { delta, reason: null },
                { withCredentials: true },
            );
            const server = r.data?.remaining_sessions;
            if (typeof server === "number") {
                setSessionsOverride((s) => ({ ...s, [id]: server }));
            }
            toast.success(
                delta > 0
                    ? `+1 session on ${b.athlete_name || "athlete"}`
                    : `−1 session on ${b.athlete_name || "athlete"}`,
                { description: r.data?.pack_label ? `Applied to “${r.data.pack_label}”` : undefined },
            );
            // Ask the parent page to refresh its bookings snapshot so any
            // OTHER booking rows for this family also reflect the new pool.
            onBookingsChanged?.();
        } catch (e) {
            // Revert optimistic write.
            setSessionsOverride((s) => {
                const next = { ...s };
                if (b.remaining_sessions != null) next[id] = b.remaining_sessions;
                else delete next[id];
                return next;
            });
            const detail = e?.response?.data?.detail;
            const msg = typeof detail === "object" ? (detail.message || detail.code) : (detail || e?.message);
            toast.error("Adjustment failed", { description: typeof msg === "string" ? msg : "Unknown error" });
        } finally {
            setAdjusting((a) => ({ ...a, [id]: false }));
        }
    };

    const memberOptions = members.filter((m) => (m.athletes || []).length > 0);
    const selectedMember = memberOptions.find((m) => m.user_id === pickerUserId);
    const selectedWalkinMember = memberOptions.find((m) => m.user_id === walkinUserId);

    const submit = async () => {
        if (!pickerAthleteId) {
            toast.error("Pick an athlete");
            return;
        }
        await onAssign(pickerAthleteId, pickerUserId);
        setPickerUserId("");
        setPickerAthleteId("");
    };

    const submitWalkin = async () => {
        if (!walkinAthleteId) {
            toast.error("Pick an athlete");
            return;
        }
        const ath = (selectedWalkinMember?.athletes || []).find((a) => a.athlete_id === walkinAthleteId);
        const athName = ath ? `${ath.first_name} ${ath.last_name || ""}`.trim() : "Athlete";
        await onWalkIn(slot.slot_id, walkinAthleteId, athName);
        setWalkinUserId("");
        setWalkinAthleteId("");
    };

    return (
        <ModalShell onClose={onClose} testId="schedule-slot-detail" title={`${slot.date} · ${formatTimeDisplay(slot.start_time)}`}>
            <div className="flex items-center gap-3 flex-wrap text-sm text-zinc-200">
                {isLive && (
                    <span
                        data-testid="slot-modal-now-pill"
                        className="inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-0.5 bg-brand-red text-white rounded-sm animate-pulse"
                    >
                        ● LIVE NOW
                    </span>
                )}
                <span>
                    {slot.label || (slot.category ? CATEGORY_LABELS[slot.category] : "Session")} · {slot.duration_minutes}min ·
                    {" "}<span className={slot.booking_count >= slot.capacity ? "text-brand-navy-300" : "text-zinc-300"}>
                        {slot.booking_count}/{slot.capacity}
                    </span> booked · cancel window {slot.cancel_window_hours}h
                </span>
            </div>

            <div className="border border-zinc-800 rounded-lg divide-y divide-zinc-800">
                {(slot.bookings || []).length === 0 ? (
                    <p className="px-4 py-3 text-sm text-zinc-300">No athletes booked yet.</p>
                ) : slot.bookings.map((b) => {
                    const checkedIn = !!b.checked_in_at;
                    const rawRemaining = typeof b.remaining_sessions === "number" ? b.remaining_sessions : null;
                    // Optimistic override takes precedence over the server
                    // value until the next slot refresh — makes the +/-
                    // arrows feel instantaneous.
                    const override = sessionsOverride[b.booking_id];
                    const remaining = override != null ? override : rawRemaining;
                    const isAdjusting = !!adjusting[b.booking_id];
                    return (
                        <div
                            key={b.booking_id}
                            data-testid={`coach-booking-${b.booking_id}`}
                            data-checked-in={checkedIn ? "1" : "0"}
                            className={`px-4 py-3 flex items-center justify-between gap-3 ${
                                checkedIn ? "bg-emerald-950/20" : ""
                            }`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap text-sm">
                                    <AthleteNameLink
                                        athleteId={b.athlete_id}
                                        name={b.athlete_name || b.athlete_id}
                                        testid={`booking-name-${b.booking_id}`}
                                        className="font-medium text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                                    />
                                    {b.is_walk_in && (
                                        <span className="text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border border-amber-500/60 text-amber-300 rounded-sm">
                                            Walk-in
                                        </span>
                                    )}
                                    {checkedIn ? (
                                        <span
                                            data-testid={`booking-checked-in-${b.booking_id}`}
                                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 bg-emerald-700/60 text-emerald-100 rounded-sm"
                                        >
                                            <CheckCircle2 className="w-3 h-3" /> Present
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center text-[10px] tracking-[0.2em] uppercase px-2 py-0.5 border border-zinc-700 text-zinc-300 rounded-sm">
                                            Booked
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1 text-[11px] text-zinc-400 flex items-center gap-2">
                                    {remaining === null ? (
                                        <span className="text-zinc-500">No pack data</span>
                                    ) : (
                                        <span
                                            data-testid={`booking-sessions-remaining-${b.booking_id}`}
                                            className="inline-flex items-center gap-1.5"
                                        >
                                            <button
                                                type="button"
                                                data-testid={`booking-sessions-decrement-${b.booking_id}`}
                                                onClick={() => adjustSessions(b, -1)}
                                                disabled={isAdjusting || remaining <= 0}
                                                aria-label="Take one session away"
                                                title="Take one session away"
                                                className="w-6 h-6 inline-flex items-center justify-center rounded border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <Minus className="w-3 h-3" />
                                            </button>
                                            <span
                                                className={`font-mono min-w-[3.5rem] text-center ${
                                                    remaining === 0 ? "text-rose-300 font-semibold" : "text-zinc-200"
                                                }`}
                                            >
                                                {isAdjusting ? (
                                                    <Loader2 className="w-3 h-3 animate-spin inline" />
                                                ) : (
                                                    `${remaining} left`
                                                )}
                                            </span>
                                            <button
                                                type="button"
                                                data-testid={`booking-sessions-increment-${b.booking_id}`}
                                                onClick={() => adjustSessions(b, +1)}
                                                disabled={isAdjusting}
                                                aria-label="Give one session back"
                                                title="Give one session back"
                                                className="w-6 h-6 inline-flex items-center justify-center rounded border border-zinc-700 text-zinc-300 hover:text-white hover:border-zinc-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                            >
                                                <Plus className="w-3 h-3" />
                                            </button>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {!checkedIn && (
                                    <button
                                        type="button"
                                        data-testid={`booking-mark-present-${b.booking_id}`}
                                        onClick={() => onMarkPresent(b)}
                                        className="text-[10px] tracking-[0.2em] uppercase px-3 py-2 bg-brand-red text-white hover:bg-brand-red/80 transition-colors"
                                    >
                                        ✓ Mark Present
                                    </button>
                                )}
                                {!checkedIn && sameDaySlots.length > 0 && (
                                    <select
                                        data-testid={`booking-move-select-${b.booking_id}`}
                                        defaultValue=""
                                        title="Move to another time today"
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            e.target.value = "";
                                            if (v) onMove(b.booking_id, v);
                                        }}
                                        className="bg-black border border-zinc-700 text-zinc-200 text-xs px-2 py-1.5 max-w-[140px] focus:outline-none focus:border-brand-red"
                                    >
                                        <option value="">Move to…</option>
                                        {sameDaySlots.map((s) => (
                                            <option key={s.slot_id} value={s.slot_id}>
                                                {formatTimeDisplay(s.start_time)} ({s.booking_count}/{s.capacity})
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {!checkedIn && sameDaySlots.length === 0 && (
                                    <button
                                        type="button"
                                        data-testid={`booking-move-${b.booking_id}`}
                                        onClick={() => setMovingBooking(b)}
                                        title="Move to another slot"
                                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase px-3 py-2 border border-zinc-700 text-zinc-200 hover:text-white hover:border-zinc-500 transition-colors"
                                    >
                                        <ArrowRightLeft className="w-3 h-3" /> Move
                                    </button>
                                )}
                                <button
                                    type="button"
                                    data-testid={`coach-booking-cancel-${b.booking_id}`}
                                    onClick={() => onCancel(b.booking_id)}
                                    className="text-xs text-zinc-400 hover:text-brand-red"
                                >
                                    {checkedIn ? "Undo" : "Remove"}
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Walk-in card — always visible, even if the slot is "full".
                Coaches occasionally squeeze a drop-in into a full class. */}
            <div className="border border-amber-700/40 bg-amber-950/20 rounded-lg p-4 space-y-3">
                <div className="text-xs text-amber-200 uppercase tracking-wider flex items-center gap-2 font-semibold">
                    <Zap className="w-3.5 h-3.5" /> Drop-in / walk-in (creates booking + deducts session)
                </div>
                {memberOptions.length === 0 ? (
                    <div className="text-sm text-zinc-300 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> No members with athletes yet.
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-2">
                        <select
                            data-testid="walkin-member"
                            className={inputClass}
                            value={walkinUserId}
                            onChange={(e) => { setWalkinUserId(e.target.value); setWalkinAthleteId(""); }}
                        >
                            <option value="">— Parent / member —</option>
                            {memberOptions.map((m) => (
                                <option key={m.user_id} value={m.user_id}>{m.name}</option>
                            ))}
                        </select>
                        <select
                            data-testid="walkin-athlete"
                            className={inputClass}
                            value={walkinAthleteId}
                            onChange={(e) => setWalkinAthleteId(e.target.value)}
                            disabled={!selectedWalkinMember}
                        >
                            <option value="">— Athlete —</option>
                            {(selectedWalkinMember?.athletes || []).map((a) => (
                                <option key={a.athlete_id} value={a.athlete_id}>
                                    {a.first_name} {a.last_name}
                                </option>
                            ))}
                        </select>
                        <button
                            type="button"
                            data-testid="walkin-submit"
                            onClick={submitWalkin}
                            disabled={!walkinAthleteId}
                            className="col-span-2 inline-flex items-center justify-center gap-2 px-3 py-2 bg-amber-500 text-black font-bold tracking-[0.15em] uppercase text-xs hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <Zap className="w-3.5 h-3.5" /> Add walk-in + check in
                        </button>
                    </div>
                )}
            </div>

            {slot.booking_count < slot.capacity && (
                <div className="border border-dashed border-zinc-800 rounded-lg p-4 space-y-3">
                    <div className="text-xs text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                        <UserPlus className="w-3.5 h-3.5" /> Assign an athlete (pre-book, no check-in yet)
                    </div>
                    {memberOptions.length === 0 ? (
                        <div className="text-sm text-zinc-300 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" /> No members with athletes yet.
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-2">
                            <select
                                data-testid="assign-member"
                                className={inputClass}
                                value={pickerUserId}
                                onChange={(e) => { setPickerUserId(e.target.value); setPickerAthleteId(""); }}
                            >
                                <option value="">— Member —</option>
                                {memberOptions.map((m) => (
                                    <option key={m.user_id} value={m.user_id}>{m.name}</option>
                                ))}
                            </select>
                            <select
                                data-testid="assign-athlete"
                                className={inputClass}
                                value={pickerAthleteId}
                                onChange={(e) => setPickerAthleteId(e.target.value)}
                                disabled={!selectedMember}
                            >
                                <option value="">— Athlete —</option>
                                {(selectedMember?.athletes || []).map((a) => (
                                    <option key={a.athlete_id} value={a.athlete_id}>
                                        {a.first_name} {a.last_name}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                data-testid="assign-submit"
                                onClick={submit}
                                disabled={!pickerAthleteId}
                                className="btn-tactical col-span-2 disabled:opacity-50"
                            >
                                Assign
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className="flex justify-between items-center pt-2">
                <div className="flex gap-2 flex-wrap">
                    <button type="button" data-testid="slot-edit" onClick={onEdit} className="btn-ghost-tactical">Edit</button>
                    <button type="button" data-testid="slot-bulk-recurring" onClick={() => setBulkOpen(true)} className="btn-ghost-tactical">
                        Bulk add (recurring)
                    </button>
                    <button type="button" data-testid="slot-delete" onClick={onDelete} className="text-sm text-zinc-300 hover:text-brand-red px-3">Delete slot</button>
                </div>
                <button type="button" onClick={onClose} className="btn-ghost-tactical">Close</button>
            </div>

            {bulkOpen && (
                <BulkRecurringAddModal
                    slot={slot}
                    members={members}
                    onClose={() => setBulkOpen(false)}
                    onDone={() => setBulkOpen(false)}
                />
            )}

            {movingBooking && (
                <MoveBookingModal
                    booking={movingBooking}
                    currentSlot={slot}
                    onClose={() => setMovingBooking(null)}
                    onConfirm={async (targetSlotId) => {
                        await onMove(movingBooking.booking_id, targetSlotId);
                        setMovingBooking(null);
                    }}
                />
            )}
        </ModalShell>
    );
}

// One-tap "Move to another slot". Day-first picker: a horizontal row of
// upcoming days (next 4 weeks) that have open slots; tap a day to reveal that
// day's open times. Moves this occurrence only — recurring templates untouched.
export function MoveBookingModal({ booking, currentSlot, onClose, onConfirm }) {
    const [slots, setSlots] = useState(null);   // null = loading
    const [busy, setBusy] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const today = new Date();
                const to = new Date();
                to.setDate(to.getDate() + 28);
                const r = await axios.get(`${API}/coach/schedule/slots`, {
                    params: { from_date: isoDate(today), to_date: isoDate(to) },
                    withCredentials: true,
                });
                setSlots(r.data || []);
            } catch (e) {
                toast.error("Couldn't load slots", { description: e?.response?.data?.detail });
                setSlots([]);
            }
        })();
    }, []);

    // Candidate slots: not the current one, has open capacity.
    const candidates = (slots || []).filter(
        (s) => s.slot_id !== currentSlot.slot_id && (s.booking_count || 0) < s.capacity,
    );

    // Group candidates by date → sorted list of { date, slots[] }.
    const days = useMemo(() => {
        const map = {};
        candidates.forEach((s) => { (map[s.date] = map[s.date] || []).push(s); });
        return Object.keys(map).sort().map((date) => ({
            date,
            slots: map[date].slice().sort((a, b) => a.start_time.localeCompare(b.start_time)),
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slots]);

    // Auto-select the first available day once loaded (or if the current pick vanished).
    useEffect(() => {
        if (days.length && !days.some((d) => d.date === selectedDate)) {
            setSelectedDate(days[0].date);
        }
    }, [days, selectedDate]);

    const activeDay = days.find((d) => d.date === selectedDate);

    const dayPill = (dateStr) => {
        const d = new Date(`${dateStr}T00:00:00`);
        return {
            wd: d.toLocaleDateString(undefined, { weekday: "short" }),
            md: d.toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        };
    };

    const pick = async (s) => {
        if (busy) return;
        setBusy(true);
        try {
            await onConfirm(s.slot_id);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="move-booking-modal">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-lg w-full p-6 max-h-[90vh] flex flex-col">
                <h3 className="text-lg font-semibold text-white mb-1">
                    Move {booking.athlete_name || "athlete"} to another slot
                </h3>
                <p className="text-xs text-zinc-500 mb-3">
                    Moving from <b>{currentSlot.date} · {formatTimeDisplay(currentSlot.start_time)}</b>. This changes only this one session — their weekly recurring slot stays the same.
                </p>

                {slots === null ? (
                    <p className="px-3 py-10 text-center text-sm text-zinc-500 flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading slots…
                    </p>
                ) : days.length === 0 ? (
                    <p className="px-3 py-10 text-center text-sm text-zinc-500" data-testid="move-no-slots">
                        No open upcoming slots to move into.
                    </p>
                ) : (
                    <>
                        {/* Day selector — horizontal row of upcoming days with open slots */}
                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5">Pick a day</div>
                        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="move-day-row">
                            {days.map((d) => {
                                const { wd, md } = dayPill(d.date);
                                const active = d.date === selectedDate;
                                return (
                                    <button
                                        key={d.date}
                                        type="button"
                                        data-testid={`move-day-${d.date}`}
                                        onClick={() => setSelectedDate(d.date)}
                                        className={`shrink-0 w-[70px] rounded-lg border px-2 py-2 text-center transition-colors ${
                                            active
                                                ? "bg-brand-red border-brand-red text-white"
                                                : "bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500"
                                        }`}
                                    >
                                        <div className="text-[10px] uppercase tracking-wide opacity-80">{wd}</div>
                                        <div className="text-sm font-semibold leading-tight">{md}</div>
                                        <div className={`text-[9px] mt-0.5 ${active ? "text-white/80" : "text-zinc-500"}`}>
                                            {d.slots.length} open
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Times within the selected day */}
                        <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mt-3 mb-1.5">Pick a time</div>
                        <div className="overflow-y-auto flex-1 grid grid-cols-2 gap-2 pr-1" data-testid="move-time-grid">
                            {(activeDay?.slots || []).map((s) => (
                                <button
                                    key={s.slot_id}
                                    type="button"
                                    onClick={() => pick(s)}
                                    disabled={busy}
                                    data-testid={`move-target-${s.slot_id}`}
                                    className="text-left rounded-lg border border-zinc-800 bg-zinc-900/60 hover:border-brand-red hover:bg-zinc-900 disabled:opacity-50 px-3 py-2.5 transition-colors"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-sm font-semibold text-white">{formatTimeDisplay(s.start_time)}</span>
                                        <span className="text-[10px] font-mono text-zinc-400 shrink-0">{s.booking_count}/{s.capacity}</span>
                                    </div>
                                    <div className="text-[11px] text-zinc-500 truncate mt-0.5">
                                        {s.label || (s.category ? CATEGORY_LABELS[s.category] : "Session")}
                                    </div>
                                </button>
                            ))}
                        </div>
                    </>
                )}

                <div className="flex justify-end gap-2 mt-4">
                    <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-zinc-400 hover:text-white" data-testid="move-cancel">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

export function BulkRecurringAddModal({ slot, members, onClose, onDone }) {
    const [picked, setPicked] = useState(() => new Set());
    const [filter, setFilter] = useState("");
    const [busy, setBusy] = useState(false);

    // Derive weekday + start_time from the slot
    const [yy, mm, dd] = slot.date.split("-").map(Number);
    const jsDow = new Date(yy, mm - 1, dd).getDay();  // 0=Sun, 1=Mon, ... 6=Sat
    const weekday = (jsDow + 6) % 7;  // backend uses 0=Mon … 6=Sun

    // Flatten athletes from all members
    const allAthletes = [];
    members.forEach((m) => {
        (m.athletes || []).forEach((a) => allAthletes.push({
            ...a,
            parent_name: m.name,
            parent_user_id: m.user_id,
        }));
    });
    const f = filter.toLowerCase();
    const filtered = !f ? allAthletes : allAthletes.filter((a) =>
        `${a.first_name} ${a.last_name || ""} ${a.parent_name}`.toLowerCase().includes(f)
    );

    const toggle = (athlete_id) => {
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(athlete_id)) next.delete(athlete_id);
            else next.add(athlete_id);
            return next;
        });
    };

    const submit = async () => {
        if (picked.size === 0) { toast.error("Pick at least one athlete"); return; }
        setBusy(true);
        try {
            const r = await axios.post(`${API}/coach/recurring-templates/bulk-add-slot`, {
                athlete_ids: Array.from(picked),
                weekday,
                start_time: slot.start_time,
                label: slot.label || null,
            }, { withCredentials: true });
            const d = r.data;
            const parts = [];
            if (d.added) parts.push(`${d.added} added`);
            if (d.already_present) parts.push(`${d.already_present} already had it`);
            toast.success(parts.join(" · ") || "Done");
            onDone();
        } catch (e) {
            toast.error("Bulk add failed", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4" data-testid="bulk-recurring-modal">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-lg w-full p-6 max-h-[90vh] flex flex-col">
                <h3 className="text-lg font-semibold text-white mb-1">Add athletes to this recurring slot</h3>
                <p className="text-xs text-zinc-500 mb-3">
                    Every selected athlete will be assigned to <b>{["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][weekday]} {slot.start_time}</b> every week. Future weeks auto-materialize.
                </p>
                <input
                    type="text"
                    placeholder="Filter by name…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm mb-3"
                    data-testid="bulk-filter"
                />
                <div className="overflow-y-auto flex-1 border border-zinc-800 rounded">
                    {filtered.length === 0 ? (
                        <p className="px-3 py-6 text-center text-sm text-zinc-500">No athletes match.</p>
                    ) : (
                        filtered.map((a) => (
                            <label
                                key={a.athlete_id}
                                className="flex items-center gap-3 px-3 py-2 border-b border-zinc-900 last:border-b-0 cursor-pointer hover:bg-zinc-900"
                                data-testid={`bulk-athlete-${a.athlete_id}`}
                            >
                                <input
                                    type="checkbox"
                                    checked={picked.has(a.athlete_id)}
                                    onChange={() => toggle(a.athlete_id)}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white">{a.first_name} {a.last_name || ""}</div>
                                    <div className="text-xs text-zinc-500">{a.parent_name}</div>
                                </div>
                            </label>
                        ))
                    )}
                </div>
                <p className="text-xs text-zinc-500 mt-2" data-testid="bulk-picked-count">
                    {picked.size} selected
                </p>
                <div className="flex justify-end gap-2 mt-4">
                    <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-zinc-400 hover:text-white">Cancel</button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy || picked.size === 0}
                        className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded flex items-center gap-2"
                        data-testid="bulk-recurring-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Add {picked.size} athlete{picked.size === 1 ? "" : "s"}
                    </button>
                </div>
            </div>
        </div>
    );
}

